import type { BranchSummary, CatalogView } from '@khaddi/contracts';
import { useApi, useAsync } from '@khaddi/ui';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useCart } from './cart';

type CatalogValue = {
  catalog: CatalogView | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** The branch the customer orders from: their pick, else the first one taking online orders. */
  branch: BranchSummary | null;
};

const CatalogContext = createContext<CatalogValue | null>(null);

/** Loads the menu once for all screens; availability depends on the selected branch. */
export const CatalogProvider = ({ children }: { children: ReactNode }) => {
  const api = useApi();
  const { branchId, setBranchId } = useCart();
  const { data, loading, error, reload } = useAsync(() => api.catalog.get(branchId ?? undefined), [api, branchId]);

  const branch = useMemo(() => {
    if (!data) return null;
    return data.branches.find((b) => b.id === branchId) ?? data.branches.find((b) => b.acceptsOnlineOrders) ?? data.branches[0] ?? null;
  }, [data, branchId]);

  useEffect(() => {
    if (branch && branch.id !== branchId) setBranchId(branch.id);
  }, [branch, branchId, setBranchId]);

  const value = useMemo(() => ({ catalog: data, loading: loading && !data, error, reload, branch }), [data, loading, error, reload, branch]);
  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
};

export const useCatalog = (): CatalogValue => {
  const value = useContext(CatalogContext);
  if (!value) throw new Error('useCatalog must be used inside <CatalogProvider>');
  return value;
};
